<template>
    <div class="relative block bg-white w-full at1024:flex justify-center at1024:justify-start items-start pb-8">
        <div class="shrink-0 hidden at1024:block w-72 pl-8">
            <SeclockMultiselect
                class="mb-8 font-bold"
                :options="manufacturers?.filter((manufacturer) => manufacturer.logo)?.map((manufacturer) => ({'label': manufacturer.name,'value': manufacturer.slug}))"
                :placeholder="$t('Select A Manufacturer')"
                :searchable="true"
                :can-clear="false"
                :large="true"
                @change="navigateToShow($event)"
            />
            <SideNavAlt
                dusk="category-select"
                :sections="tags"
                :click-to-scroll="false"
                :all="true"
                v-model="selectedTag"
                header="Browse by category"
            />
        </div>
        <div class="overflow-hidden at1024:overflow-hidden at1024:w-full px-4 at1024:px-8">
            <div class="flex items-center justify-between px-4 mb-4 at1024:px-0 at1024:pb-4">
                <div v-if="!query.tag" class="w-full overflow-x-auto">
                    <AlphabetFilter v-model="query.letter" :available-letters="availableLetters"/>
                </div>
                <div v-else class="mr-6 p-3.5 grow bg-gray-125">
                    {{ $t("Category") }}:
                    <span class="font-bold">{{ selectedTag.name }}</span>
                    <button
                        class="type-secondary hover:text-black ml-6 underline underline-offset-4"
                        @click="selectedTag = null"
                    >
                        {{ $t("Clear") }}
                    </button>
                </div>
                <div class="hidden at1024:flex items-center gap-2 border-l at1350:border-l-0 border-gray-225 pl-4 fill-gray-500">
                    <button @click.prevent="displaySetting = 'grid'">
                        <BaseSvg
                            name="icon-grid-view"
                            :class="displaySetting === 'grid' ? 'fill-gray-800': 'fill-gray-400 hover:fill-gray'"
                            class="w-8 active:fill-black"
                            aria-hidden="true"
                        />
                        <span class="sr-only">View manufacturers as a grid</span>
                    </button>
                    <button @click.prevent="displaySetting = 'rows'">
                        <BaseSvg
                            name="icon-list"
                            :class="displaySetting === 'rows'? 'fill-gray-800': 'fill-gray-400 hover:fill-gray'"
                            class="w-8 active:fill-prim"
                            aria-hidden="true"
                        />
                        <span class="sr-only">View manufacturers as a list</span>
                    </button>
                </div>
                <div class="border-l border-gray-225 at1350:border-l-0 pl-4 flex at1024:hidden items-center fill-gray-500">
                    <button @click.prevent="displaySetting === 'grid'? (displaySetting = 'rows'): (displaySetting = 'grid')">
                        <BaseSvg
                            :name="displaySetting === 'rows' ? 'icon-grid-view': 'icon-list'"
                            class="w-8 fill-gray active:fill-gray-800"
                            aria-hidden="true"
                        />
                        <span class="sr-only">Toggle between list view and grid view</span>
                    </button>
                </div>
            </div>
            <ul
                dusk="manufacturers-cards"
                class="w-full px-4 at1024:px-0"
                :class="
                    displaySetting === 'grid'
                        ? 'grid gap-4 grid-cols-1 at1024:grid-cols-2 at1440:grid-cols-3'
                        : ''
                "
            >
                <template v-for="manufacturer in filteredManufacturers">
                    <li v-if="marketingAssetUrl(manufacturer.logo?.external_id)">
                        <ManufacturerCard
                            :manufacturer="manufacturer"
                            :display-setting="displaySetting"
                            :is-price-books="isPriceBooks"
                        />
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script setup>
import {
    AlphabetFilter,
    BaseSvg,
    ManufacturerCard,
    SideNavAlt,
    SeclockMultiselect,
} from "@/Components";
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAssetTransforms} from "@/Composables";
import {router, usePage} from "@inertiajs/vue3";
const props = defineProps(["manufacturers", "tags", "availableLetters","isPriceBooks"]);
const displaySetting = ref("grid");

const query = reactive(usePage().props.query ?? {});

const computedManufacturers = computed(() => props.manufacturers);
const computedTags = computed(() => props.tags);

onMounted(() => {
    console.log(computedManufacturers.value, computedTags.value);
})

const selectedTag = computed({
    get: () => {
        let tag = computedTags.value?.find((tag) => tag.name === query.tag);
        return tag;
    },
    set: (tag) => {
        query.tag = tag;
        if(tag){
            query.letter = null;
        }
    },
});

watch(query, (newQuery) => {
    router.replace(route(route().current()),{
        query: newQuery,
        preserveScroll: true,
        preserveState: true,
        preserveQuery: false,
        only: ["referer"],
    });
    // router.get(route(route().current()),
    //     query,
    //     {
    //         replace: true,
    //         preserveScroll: true,
    //         preserveState: true,
    //         preserveQuery: false,
    //         only: ["referer", "query","flash"],
    //     }
    // );
});

const { marketingAssetUrl } = useAssetTransforms();

const filteredManufacturers = computed(() => {
    let data = computedManufacturers.value;

    if (selectedTag.value) {
        return data.filter((manufacturer) =>
            manufacturer.tag_ids?.includes(selectedTag.value.id)
        );
    }

    if (query.letter) {
        return data.filter((manufacturer) =>
            manufacturer.name?.startsWith(query.letter.toLowerCase()) || manufacturer.name?.startsWith(query.letter.toUpperCase())
        );
    }

    return data;
});

const navigateToShow = (slug) => {
    let showRoute = props.isPriceBooks
        ? 'web.catalog.price-books.show'
        : 'web.catalog.manufacturers.show'
    router.visit(route(showRoute, slug));
};

</script>
